import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  fmt, applyLightingDemand, applyFixedApplianceDemand, applyRangeDemand,
  applyDryerDemand, DEMAND_FACTOR_RANGES,
} from "./electrical-helpers";
import { readSettings } from "@/hooks/use-settings";
import type { useJobLog } from "@/hooks/use-job-log";

type SaveFn = (entry: Parameters<ReturnType<typeof useJobLog>["addEntry"]>[0]) => void;
const defaults = readSettings();

export const DemandFactorCalc = ({ onSendToService, onSave }: { onSendToService?: (va: number) => void; onSave?: SaveFn }) => {
  const [method, setMethod] = useState<"standard" | "optional">("standard");
  const [sqft, setSqft] = useState("");
  const [buildingType, setBuildingType] = useState<"residential" | "commercial">("residential");
  const [numSmallApp, setNumSmallApp] = useState("2");
  const [hasLaundry, setHasLaundry] = useState(true);
  const [rangeVA, setRangeVA] = useState("");
  const [numRanges, setNumRanges] = useState("1");
  const [dryerVA, setDryerVA] = useState("");
  const [numDryers, setNumDryers] = useState("1");
  const [fixedAppVA, setFixedAppVA] = useState("");
  const [numFixedApps, setNumFixedApps] = useState("0");
  const [hvacVA, setHvacVA] = useState("");
  const [motorVA, setMotorVA] = useState("");
  const [heatVA, setHeatVA] = useState("");
  const [acVA, setAcVA] = useState("");
  const [waterHeaterVA, setWaterHeaterVA] = useState("");

  const area = parseFloat(sqft) || 0;
  const vaPerSqft = buildingType === "residential" ? 3 : 3.5;
  const generalLightingVA = area * vaPerSqft;
  const smallAppVA = (parseInt(numSmallApp) || 0) * 1500;
  const laundryVA = hasLaundry ? 1500 : 0;

  // ─── Standard Method (220 Part III/IV) ───
  const lightingCombined = generalLightingVA + smallAppVA + laundryVA;
  const lightingDemand = applyLightingDemand(lightingCombined);

  const nFixed = parseInt(numFixedApps) || 0;
  const fixedTotal = parseFloat(fixedAppVA) || 0;
  const fixedDemand = applyFixedApplianceDemand(fixedTotal, nFixed);

  const nRanges = parseInt(numRanges) || 0;
  const rangeTotal = parseFloat(rangeVA) || 0;
  const rangeDemand = nRanges > 0 && rangeTotal > 0 ? applyRangeDemand(rangeTotal, nRanges) : 0;

  const nDryers = parseInt(numDryers) || 0;
  const dryerTotal = parseFloat(dryerVA) || 0;
  const dryerDemand = nDryers > 0 && dryerTotal > 0 ? applyDryerDemand(dryerTotal, nDryers) : 0;

  const hvac = parseFloat(hvacVA) || 0;
  const motor = parseFloat(motorVA) || 0;
  const motorDemand = motor > 0 ? motor * 1.25 : 0;

  const stdTotalConnected = lightingCombined + fixedTotal + rangeTotal + dryerTotal + hvac + motor;
  const stdTotalDemand = lightingDemand + fixedDemand + rangeDemand + dryerDemand + hvac + motorDemand;
  const stdDemandFactor = stdTotalConnected > 0 ? (stdTotalDemand / stdTotalConnected) * 100 : 0;

  const stdBreakdown = [
    { label: "General Lighting + SA + Laundry", connected: lightingCombined, demand: lightingDemand, nec: "220.42" },
    ...(fixedTotal > 0 ? [{ label: `Fixed Appliances (${nFixed} units${nFixed >= 4 ? " ×75%" : ""})`, connected: fixedTotal, demand: fixedDemand, nec: "220.53" }] : []),
    ...(rangeDemand > 0 ? [{ label: `Range/Oven (${nRanges} unit${nRanges > 1 ? "s" : ""})`, connected: rangeTotal, demand: rangeDemand, nec: "220.55" }] : []),
    ...(dryerDemand > 0 ? [{ label: `Dryer (${nDryers} unit${nDryers > 1 ? "s" : ""})`, connected: dryerTotal, demand: dryerDemand, nec: "220.54" }] : []),
    ...(hvac > 0 ? [{ label: "HVAC / Heat", connected: hvac, demand: hvac, nec: "220.60" }] : []),
    ...(motorDemand > 0 ? [{ label: "Motor (125%)", connected: motor, demand: motorDemand, nec: "430.24" }] : []),
  ];

  // ─── Optional Method (220.82/220.83) ───
  const heat = parseFloat(heatVA) || 0;
  const ac = parseFloat(acVA) || 0;
  const waterHeater = parseFloat(waterHeaterVA) || 0;

  const optGeneralLoads = generalLightingVA + smallAppVA + laundryVA + fixedTotal + rangeTotal + dryerTotal + waterHeater;
  const optGeneralDemand = optGeneralLoads <= 10000
    ? optGeneralLoads
    : 10000 + (optGeneralLoads - 10000) * 0.40;

  const acDemand = ac;
  const heatDemand65 = heat * 0.65;
  const optHvacDemand = Math.max(acDemand, heatDemand65);
  const hvacNote = acDemand >= heatDemand65 ? "A/C at 100%" : "Heat at 65%";

  const optTotalConnected = optGeneralLoads + Math.max(heat, ac);
  const optTotalDemand = optGeneralDemand + optHvacDemand;
  const optDemandFactor = optTotalConnected > 0 ? (optTotalDemand / optTotalConnected) * 100 : 0;

  const optBreakdown = [
    { label: "General Loads (first 10 kVA @ 100%)", value: Math.min(optGeneralLoads, 10000), nec: "220.82(B)" },
    ...(optGeneralLoads > 10000 ? [{ label: `Remainder @ 40% (${Math.round(optGeneralLoads - 10000).toLocaleString()} VA)`, value: (optGeneralLoads - 10000) * 0.40, nec: "220.82(B)" }] : []),
    ...(optHvacDemand > 0 ? [{ label: `HVAC — ${hvacNote}`, value: optHvacDemand, nec: "220.82(C)" }] : []),
  ];

  const isStandard = method === "standard";
  const totalDemand = isStandard ? stdTotalDemand : optTotalDemand;
  const totalConnected = isStandard ? stdTotalConnected : optTotalConnected;
  const demandFactor = isStandard ? stdDemandFactor : optDemandFactor;
  const hasInput = totalDemand > 0;

  return (
    <div className="space-y-3">
      <div className="flex gap-1 bg-secondary rounded-lg p-1">
        <button onClick={() => setMethod("standard")} className={`flex-1 py-2 px-3 rounded-md text-xs font-semibold transition-all ${isStandard ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>Standard (220 Part III)</button>
        <button onClick={() => setMethod("optional")} className={`flex-1 py-2 px-3 rounded-md text-xs font-semibold transition-all ${!isStandard ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>Optional (220.82)</button>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed">
        {isStandard
          ? "NEC Article 220 Standard Method — Calculate total demand load with proper demand factors for service/feeder sizing."
          : "NEC 220.82 Optional Method — Simplified residential calculation: 100% of first 10 kVA + 40% of remainder, with HVAC at largest of A/C (100%) or heat (65%)."}
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Building Type</label><Select value={buildingType} onValueChange={(v: "residential" | "commercial") => setBuildingType(v)}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="residential">Residential (3 VA/ft²)</SelectItem><SelectItem value="commercial">Commercial (3.5 VA/ft²)</SelectItem></SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Floor Area (sq ft)</label><Input type="number" inputMode="numeric" value={sqft} onChange={e => setSqft(e.target.value)} placeholder="e.g. 2000" className="bg-secondary border-border font-mono-data text-lg" /></div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Small Appliance Ckts</label><Select value={numSmallApp} onValueChange={setNumSmallApp}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["0","1","2","3","4","5","6"].map(n => <SelectItem key={n} value={n}>{n} circuits ({parseInt(n) * 1500} VA)</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Laundry Circuit</label><Select value={hasLaundry ? "yes" : "no"} onValueChange={v => setHasLaundry(v === "yes")}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="yes">Yes (1500 VA)</SelectItem><SelectItem value="no">No</SelectItem></SelectContent></Select></div>
      </div>

      <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Large Appliances</h3>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Range/Oven (VA)</label><Input type="number" inputMode="numeric" value={rangeVA} onChange={e => setRangeVA(e.target.value)} placeholder="12000" className="bg-secondary border-border font-mono-data" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block"># Ranges</label><Select value={numRanges} onValueChange={setNumRanges}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["1","2","3","4","5","6"].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Dryer (VA)</label><Input type="number" inputMode="numeric" value={dryerVA} onChange={e => setDryerVA(e.target.value)} placeholder="5000" className="bg-secondary border-border font-mono-data" /></div>
        <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block"># Dryers</label><Select value={numDryers} onValueChange={setNumDryers}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["1","2","3","4","5","6"].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select></div>
      </div>

      {isStandard ? (
        <>
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Additional Loads</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Fixed Appliances (VA)</label><Input type="number" inputMode="numeric" value={fixedAppVA} onChange={e => setFixedAppVA(e.target.value)} placeholder="Total VA" className="bg-secondary border-border font-mono-data" /></div>
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block"># Fixed Appliances</label><Select value={numFixedApps} onValueChange={setNumFixedApps}><SelectTrigger className="bg-secondary border-border font-mono-data"><SelectValue /></SelectTrigger><SelectContent>{["0","1","2","3","4","5","6","7","8","10"].map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">HVAC / Heat (VA)</label><Input type="number" inputMode="numeric" value={hvacVA} onChange={e => setHvacVA(e.target.value)} placeholder="Larger of heat or cool" className="bg-secondary border-border font-mono-data" /></div>
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Motor Load (VA)</label><Input type="number" inputMode="numeric" value={motorVA} onChange={e => setMotorVA(e.target.value)} placeholder="Largest motor" className="bg-secondary border-border font-mono-data" /></div>
          </div>
        </>
      ) : (
        <>
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">Additional Loads</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Fixed Appliances (VA)</label><Input type="number" inputMode="numeric" value={fixedAppVA} onChange={e => setFixedAppVA(e.target.value)} placeholder="Total VA" className="bg-secondary border-border font-mono-data" /></div>
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Water Heater (VA)</label><Input type="number" inputMode="numeric" value={waterHeaterVA} onChange={e => setWaterHeaterVA(e.target.value)} placeholder="e.g. 4500" className="bg-secondary border-border font-mono-data" /></div>
          </div>
          <h3 className="text-xs text-muted-foreground font-semibold uppercase tracking-wider pt-1">HVAC — Enter Separately</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">A/C Nameplate (VA)</label><Input type="number" inputMode="numeric" value={acVA} onChange={e => setAcVA(e.target.value)} placeholder="e.g. 5000" className="bg-secondary border-border font-mono-data" /></div>
            <div><label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mb-1 block">Electric Heat (VA)</label><Input type="number" inputMode="numeric" value={heatVA} onChange={e => setHeatVA(e.target.value)} placeholder="e.g. 10000" className="bg-secondary border-border font-mono-data" /></div>
          </div>
        </>
      )}

      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <div className="trade-card border-muted/40">
            <span className="text-xs text-muted-foreground uppercase tracking-wider block mb-2">
              {isStandard ? "Standard Method Breakdown" : "Optional Method Breakdown (220.82)"}
            </span>
            <div className="space-y-1 text-xs font-mono-data">
              {isStandard ? (
                <>
                  {stdBreakdown.map((row, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-muted-foreground">{row.label} <span className="text-[10px]">({row.nec})</span></span>
                      <span>
                        {row.connected !== row.demand && <span className="text-muted-foreground line-through mr-2">{Math.round(row.connected).toLocaleString()}</span>}
                        <span className="text-foreground font-bold">{Math.round(row.demand).toLocaleString()} VA</span>
                      </span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="flex justify-between text-[10px] text-muted-foreground border-b border-border pb-1 mb-1">
                    <span>General loads total: {Math.round(optGeneralLoads).toLocaleString()} VA</span>
                    <span>(lighting + SA + laundry + appliances)</span>
                  </div>
                  {optBreakdown.map((row, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="text-muted-foreground">{row.label} <span className="text-[10px]">({row.nec})</span></span>
                      <span className="text-foreground font-bold">{Math.round(row.value).toLocaleString()} VA</span>
                    </div>
                  ))}
                </>
              )}
              <div className="flex justify-between font-bold border-t border-border pt-1.5 mt-1.5 text-sm">
                <span>Total Demand</span>
                <span className="text-primary">{Math.round(totalDemand).toLocaleString()} VA</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="trade-card text-center"><span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Connected</span><span className="font-mono-data text-sm font-bold">{Math.round(totalConnected).toLocaleString()} VA</span></div>
            <div className="trade-card text-center border-primary/20 bg-primary/5"><span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Demand</span><span className="font-mono-data text-sm font-bold text-primary">{Math.round(totalDemand).toLocaleString()} VA</span></div>
            <div className="trade-card text-center"><span className="text-[10px] text-muted-foreground uppercase tracking-wider block">Factor</span><span className="font-mono-data text-sm font-bold">{fmt(demandFactor, 1)}%</span></div>
          </div>

          {isStandard && (
            <div className="trade-card border-accent/40 bg-accent/5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">NEC 220 Demand Factor Reference</h3>
              <div className="space-y-1">
                {DEMAND_FACTOR_RANGES.map((d, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{d.label}</span>
                    <span className="font-mono-data"><span className="text-foreground font-bold">{d.factor}%</span> <span className="text-muted-foreground text-[10px]">({d.necRef})</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isStandard && (
            <div className="trade-card border-accent/40 bg-accent/5">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">220.82 Optional Method Rules</h3>
              <div className="space-y-1.5 text-xs">
                <div className="flex gap-2"><span className="font-mono-data text-primary font-semibold whitespace-nowrap">220.82(A)</span><span className="text-muted-foreground">Applies to single dwelling units served by 120/240V single-phase, 3-wire, 100A+ service</span></div>
                <div className="flex gap-2"><span className="font-mono-data text-primary font-semibold whitespace-nowrap">220.82(B)</span><span className="text-muted-foreground">General loads: 100% of first 10 kVA + 40% of remainder (includes lighting, SA, laundry, appliances, ranges, dryers)</span></div>
                <div className="flex gap-2"><span className="font-mono-data text-primary font-semibold whitespace-nowrap">220.82(C)</span><span className="text-muted-foreground">HVAC: largest of — A/C at 100%, heat ≤4 units at 100%, heat ≥5 units at 40%, central heat at 65%</span></div>
              </div>
            </div>
          )}

          {stdTotalDemand > 0 && optTotalDemand > 0 && (
            <div className="trade-card border-muted/40">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Method Comparison</h3>
              <div className="grid grid-cols-2 gap-y-2 text-sm">
                <span className="text-muted-foreground">Standard (220 Part III):</span>
                <span className={`font-mono-data font-bold text-right ${isStandard ? "text-primary" : "text-foreground"}`}>{Math.round(stdTotalDemand).toLocaleString()} VA</span>
                <span className="text-muted-foreground">Optional (220.82):</span>
                <span className={`font-mono-data font-bold text-right ${!isStandard ? "text-primary" : "text-foreground"}`}>{Math.round(optTotalDemand).toLocaleString()} VA</span>
                <span className="text-muted-foreground">Difference:</span>
                <span className={`font-mono-data font-bold text-right ${optTotalDemand < stdTotalDemand ? "text-pipe-green" : "text-destructive"}`}>
                  {optTotalDemand < stdTotalDemand ? "−" : "+"}{Math.abs(Math.round(stdTotalDemand - optTotalDemand)).toLocaleString()} VA
                  <span className="text-muted-foreground text-[10px] ml-1">({fmt(Math.abs(1 - optTotalDemand / stdTotalDemand) * 100, 1)}%)</span>
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">💡 The optional method often yields a lower demand for residential — you may use whichever is permitted by the AHJ.</p>
            </div>
          )}
        </div>
      )}

      {hasInput && onSendToService && (
        <button
          onClick={() => onSendToService(Math.round(totalDemand))}
          className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-sm shadow-sm hover:bg-primary/90 transition-colors"
        >
          → Size Service Entrance ({Math.round(totalDemand).toLocaleString()} VA)
        </button>
      )}

      {hasInput && onSave && (
        <button type="button" onClick={() => {
          onSave({
            calcType: "demand-factor", label: `${isStandard ? "Standard" : "Optional"} Demand — ${Math.round(totalDemand).toLocaleString()} VA`,
            inputs: { method: isStandard ? "Standard (220 III)" : "Optional (220.82)", building: buildingType, sqft: sqft || "—" },
            results: [
              { label: "Connected", value: Math.round(totalConnected).toLocaleString(), unit: "VA" },
              { label: "Demand", value: Math.round(totalDemand).toLocaleString(), unit: "VA" },
              { label: "Demand Factor", value: fmt(demandFactor, 1), unit: "%" },
            ],
            note: "",
          });
          toast.success("Saved to Job Log");
        }} className="w-full h-9 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">💾 Save to Job Log</button>
      )}

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-2">
        {isStandard
          ? "⚠️ Standard method per NEC Article 220 Part III/IV. Verify with local AHJ."
          : "⚠️ Optional method per NEC 220.82 for existing single dwelling units with 100A+ service. For new construction, see 220.83. Verify with local AHJ."}
      </p>
    </div>
  );
};
